import { memo, useState, useCallback, useMemo } from "react";

import { type Maybe } from "@shopify/hydrogen-react/storefront-api-types";

import { useModalAria } from "@react/a11y/modalAria";
import { useProductSizeGuide } from "@react/hooks/useProductSizeGuide";
import { ModalProvider } from "@sections/ModalProvider";
import { SizeGuideModalView } from "@sections/ModalProvider/SizeGuideModal/SizeGuideModal.view";
import { useSectionSettings } from "@sections/Root/SectionSettingsContext";
import {
  PageModal,
  type ProductHeroSettings,
  type OPProduct,
  type SizeGuideDimensionsUnit,
  type StandardizedSizeGuideData,
  type CartProductCrossProductBridge,
} from "@tablescape/state";
import {
  getShopifyGlobalId,
  checkIfHeightString,
  getInchValueFromHeightString,
} from "@tablescape/utilities";

const resolveConvertibleFieldValue = (
  value: string | number | string[] | undefined,
  unit: SizeGuideDimensionsUnit
) => {
  if (typeof value !== "string" && typeof value !== "number") return "";

  const stringValue = value.toString();

  if (stringValue.includes("-")) {
    return stringValue
      .split("-")
      .map((str) => {
        return unit === "in" ? str : (Number(str) * 2.54).toFixed(1);
      })
      .join("-");
  }
  const inchValue = checkIfHeightString(stringValue)
    ? getInchValueFromHeightString(stringValue)
    : Number(stringValue);
  const unitSign =
    checkIfHeightString(stringValue) && unit === "cm" ? "cm" : "";
  return (
    (isNaN(inchValue) || unit === "in"
      ? value
      : (inchValue * 2.54).toFixed(1)) + unitSign
  );
};

type SizeGuideModalBaseProps = {
  product?: OPProduct;
};

const SizeGuideModalBase = ({ product }: SizeGuideModalBaseProps) => {
  const {
    crossProductBridge: legacyCrossProductBridge,
    meta: {
      localization: { country, language },
    },
    productId: pdpProductId,
    sizeGuide: { defaultDimensionUnit, image, modelInfo, tableHeadings, title },
    text: { closeBtnAlt, toggleAlt },
  } = useSectionSettings<
    {
      crossProductBridge?: Maybe<CartProductCrossProductBridge>;
    } & ProductHeroSettings
  >();

  const resolvedCrossProductBridge = useMemo(
    () => product?.crossProductBridge ?? legacyCrossProductBridge ?? undefined,
    [legacyCrossProductBridge, product?.crossProductBridge]
  );

  const crossProductBridgeLabel = useMemo(() => {
    return resolvedCrossProductBridge?.label ?? "";
  }, [resolvedCrossProductBridge?.label]);

  const crossProductBridgeOptions = useMemo(
    () =>
      resolvedCrossProductBridge?.options.map(
        (option: { label: string }) => option.label
      ),
    [resolvedCrossProductBridge?.options]
  );

  const currentProductIndex = useMemo(() => {
    return (
      resolvedCrossProductBridge?.options.findIndex(
        (option) =>
          option.product.id ===
          (product?.id ?? getShopifyGlobalId("Product", pdpProductId))
      ) ?? -1
    );
  }, [pdpProductId, product?.id, resolvedCrossProductBridge?.options]);

  const [unit, setUnit] =
    useState<SizeGuideDimensionsUnit>(defaultDimensionUnit);
  const convertUnit = useCallback(
    (unit: SizeGuideDimensionsUnit) => setUnit(unit),
    []
  );

  const crossProductSizeGuideData: StandardizedSizeGuideData[][] | undefined =
    useProductSizeGuide({
      productIds: resolvedCrossProductBridge?.options.map(
        (option) => option.product.id
      ),
      country,
      language,
    });

  const convertedCrossProductSizeGuideData = useMemo(() => {
    return (
      crossProductSizeGuideData?.map(
        (productSizeGuideData: StandardizedSizeGuideData[]) =>
          productSizeGuideData
            .filter((sizeGuideMetaobject: StandardizedSizeGuideData) =>
              ["dimensions", "custom"].includes(sizeGuideMetaobject.type)
            )
            .map(
              (
                sizeGuideMetaobject: StandardizedSizeGuideData
              ): StandardizedSizeGuideData => {
                if (sizeGuideMetaobject.type === "dimensions") {
                  const convertedFields: Record<string, string | string[]> = {};
                  Object.keys(sizeGuideMetaobject.fields).forEach((field) => {
                    convertedFields[field] =
                      sizeGuideMetaobject.dimensionsDataFields.includes(field)
                        ? resolveConvertibleFieldValue(
                            sizeGuideMetaobject.fields[field],
                            unit
                          )
                        : sizeGuideMetaobject.fields[field];
                  });
                  return {
                    ...sizeGuideMetaobject,
                    fields: convertedFields,
                  };
                }
                return sizeGuideMetaobject;
              }
            )
      ) ?? []
    );
  }, [crossProductSizeGuideData, unit]);

  const currentProductSizeGuideData: StandardizedSizeGuideData[] | undefined =
    useMemo(
      () =>
        (crossProductSizeGuideData &&
          crossProductSizeGuideData[currentProductIndex]) ??
        undefined,
      [crossProductSizeGuideData, currentProductIndex]
    );

  const getLocalizedHeading = useCallback(
    (field: string): string => {
      if (!tableHeadings) return field;

      // Type-safe lookup using the specific DimensionField keys
      switch (field) {
        case "depth":
          return tableHeadings.depth ?? field;
        case "diameter":
          return tableHeadings.diameter ?? field;
        case "height":
          return tableHeadings.height ?? field;
        case "length":
          return tableHeadings.length ?? field;
        default:
          return field;
      }
    },
    [tableHeadings]
  );

  const sizeGuideTableFields = useMemo(() => {
    // Type-safe field heading lookup
    return currentProductSizeGuideData
      ?.reduce<string[]>(
        (fields, sizeGuideMetaobject: StandardizedSizeGuideData) => {
          if (
            sizeGuideMetaobject.type === "dimensions" &&
            sizeGuideMetaobject.dimensionsDataFields
          ) {
            return [
              ...fields,
              ...sizeGuideMetaobject.dimensionsDataFields.map(
                getLocalizedHeading
              ),
            ];
          }
          if (
            sizeGuideMetaobject.type === "custom" &&
            sizeGuideMetaobject.customDataLabel
          ) {
            return [...fields, sizeGuideMetaobject.customDataLabel];
          }
          return fields;
        },
        []
      )
      .filter((field): field is string => !!field);
  }, [currentProductSizeGuideData, getLocalizedHeading]);

  const modelDetails = useMemo(() => {
    const modelMetaobject = currentProductSizeGuideData?.find(
      (metaobject) => metaobject.type === "model"
    );
    if (!modelInfo || !modelMetaobject) return undefined;
    return {
      heading: modelInfo.heading,
      content: modelInfo.content
        .split(" ")
        .map((word: string) => {
          return word.startsWith("[") && word.endsWith("]")
            ? resolveConvertibleFieldValue(
                modelMetaobject.fields[word.slice(1, -1)],
                unit
              )
            : word;
        })
        .join(" "),
      table: modelInfo.tableFields.map((field) => [
        field,
        resolveConvertibleFieldValue(modelMetaobject.fields[field], unit),
      ]),
    };
  }, [currentProductSizeGuideData, modelInfo, unit]);

  const modalAria = useModalAria({
    title: title || "Size Guide",
  });

  if (
    !sizeGuideTableFields ||
    !crossProductBridgeOptions ||
    !convertedCrossProductSizeGuideData
  )
    return null;

  return (
    <ModalProvider
      render={({ closeModal, modalProps }) => (
        <SizeGuideModalView
          closeBtnAlt={closeBtnAlt}
          closeModal={closeModal}
          modalAria={modalAria}
          modalProps={modalProps}
          sizeGuideTableFields={sizeGuideTableFields}
          sizeGuideTableLabel={crossProductBridgeLabel ?? ""}
          sizeGuideTableOptions={crossProductBridgeOptions}
          sizeGuideTableValues={convertedCrossProductSizeGuideData}
          title={title}
          toggleUnit={convertUnit}
          unit={unit}
          {...(image && {
            image,
          })}
          {...(modelDetails && {
            modelDetails,
          })}
          toggleAlt={toggleAlt}
        />
      )}
      targetModal={PageModal.SIZE_GUIDE_MODAL}
    />
  );
};

export const SizeGuideModal = memo(SizeGuideModalBase);
